import client from '@config/client'
import API_URL from '@config/services'
import { ApiClientResult } from '@/types/api'

interface LeasePayload {
  title?: string
  description?: string
  tenant_id?: number | string
  operation_id?: number | string
  start_date?: string
  end_date?: string
  status?: string
  [key: string]: any
}

interface PeriodPayload {
  name?: string
  start_date?: string
  end_date?: string
  monthly_amount?: number
  [key: string]: any
}

interface LeaseArticlePayload {
  title?: string
  content?: string
  [key: string]: any
}

/**
 * Creates a new lease
 * @param payload - Lease data
 * @returns API response
 */
export const onCreateLease = (
  payload: LeasePayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.LEASES.POST_LEASE(), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates a lease
 * @param leaseId - Lease ID
 * @param payload - Lease data to update
 * @returns API response
 */
export const onUpdateLease = (
  leaseId: number | string,
  payload: Partial<LeasePayload>
): Promise<ApiClientResult> => {
  return client
    .patch(`/leases/${leaseId}/`, {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a lease
 * @param leaseId - Lease ID
 * @returns API response
 */
export const onDeleteLease = (
  leaseId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(`/leases/${leaseId}/`)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Creates a new lease period
 * @param leaseId - Lease ID
 * @param payload - Period data
 * @returns API response
 */
export const onCreatePeriod = (
  leaseId: number | string,
  payload: PeriodPayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.LEASES.GET_LEASE_PERIODS(leaseId), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates a lease period
 * @param leaseId - Lease ID
 * @param periodId - Period ID
 * @param payload - Period data to update
 * @returns API response
 */
export const onUpdatePeriod = (
  leaseId: number | string,
  periodId: number | string,
  payload: Partial<PeriodPayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.LEASES.PATCH_LEASE_PERIOD(leaseId, periodId), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a lease period
 * @param leaseId - Lease ID
 * @param periodId - Period ID
 * @returns API response
 */
export const onDeletePeriod = (
  leaseId: number | string,
  periodId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.LEASES.DELETE_LEASE_PERIOD(leaseId, periodId))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Creates a new lease article
 * @param leaseId - Lease ID
 * @param payload - Article data
 * @returns API response
 */
export const onCreateLeaseArticle = (
  leaseId: number | string,
  payload: LeaseArticlePayload
): Promise<ApiClientResult> => {
  return client
    .post(API_URL.LEASES.POST_LEASE_ARTICLES(leaseId), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Updates a lease article
 * @param leaseId - Lease ID
 * @param articleId - Article ID
 * @param payload - Article data to update
 * @returns API response
 */
export const onUpdateLeaseArticle = (
  leaseId: number | string,
  articleId: number | string,
  payload: Partial<LeaseArticlePayload>
): Promise<ApiClientResult> => {
  return client
    .patch(API_URL.LEASES.DELETE_LEASE_ARTICLE(leaseId, articleId), {
      ...payload
    })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Deletes a lease article
 * @param leaseId - Lease ID
 * @param articleId - Article ID
 * @returns API response
 */
export const onDeleteLeaseArticle = (
  leaseId: number | string,
  articleId: number | string
): Promise<ApiClientResult> => {
  return client
    .delete(API_URL.LEASES.DELETE_LEASE_ARTICLE(leaseId, articleId))
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets all lease periods
 * @returns API response with periods list
 */
export const onGetLeasesPeriods = (): Promise<ApiClientResult> => {
  return client
    .get(API_URL.LEASES.GET_LEASES_PERIODS())
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Gets active leases for an operation
 * @param id - Operation ID
 * @returns API response with leases list
 */
export const onGetLeasesActive = (
  id: number | string
): Promise<ApiClientResult> => {
  return client
    .get(`/leases/?operation=${id}&status=active`)
    .then(response => response)
    .catch(error => {
      return error.response
    })
}
